import { getSupabaseClient } from '@/integrations/supabase/client';
import type { ProductModifierIngredient, SelectedProductModifier, TabItemModifier } from '@/types';
import { ingredientsService } from '@/services/ingredients/ingredientsService';
import { productModifiersService } from './productModifiersService';

/** Embedded relation for `tab_items` selects. */
export const TAB_ITEM_MODIFIERS_EMBED = `
  tab_item_modifiers (
    id,
    tab_item_id,
    modifier_id,
    name,
    price
  )
`;

export type TabItemModifierRow = {
  id: string;
  tab_item_id: string;
  modifier_id: string | null;
  name: string;
  price: number | string | null;
};

export const mapTabItemModifierRows = (
  rows: TabItemModifierRow[] | null | undefined
): TabItemModifier[] | undefined => {
  const modifiers = (rows ?? [])
    .filter((row) => row != null)
    .map((row) => ({
      id: row.id,
      tabItemId: row.tab_item_id,
      modifierId: row.modifier_id ?? null,
      name: row.name,
      price: Number(row.price ?? 0),
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  return modifiers.length ? modifiers : undefined;
};

/**
 * Adds `units` applications of each modifier's recipe into `usage`
 * (ingredientId → quantity). Modifiers without a recipe contribute nothing.
 */
export const addModifierIngredientUsage = (
  usage: Map<string, number>,
  recipes: Map<string, ProductModifierIngredient[]>,
  modifierIds: Array<string | null>,
  units: number
) => {
  for (const modifierId of modifierIds) {
    if (!modifierId) continue;
    for (const ingredient of recipes.get(modifierId) ?? []) {
      usage.set(
        ingredient.ingredientId,
        (usage.get(ingredient.ingredientId) ?? 0) + ingredient.quantity * units
      );
    }
  }
};

export const tabItemModifiersService = {
  /** Idempotent: a replayed insert for the same line/modifier is ignored. */
  async insertForTabItem(
    tabItemId: string,
    organizationId: string,
    modifiers: Array<Pick<SelectedProductModifier, 'name' | 'price'> & { modifierId: string | null }>
  ) {
    if (modifiers.length === 0) return;
    const supabase = getSupabaseClient();
    const rows = modifiers.map((modifier) => ({
      tab_item_id: tabItemId,
      modifier_id: modifier.modifierId,
      name: modifier.name,
      price: modifier.price,
      organization_id: organizationId,
    }));
    const { error } = await supabase
      .from('tab_item_modifiers')
      .upsert(rows, { onConflict: 'tab_item_id,modifier_id', ignoreDuplicates: true });
    if (error) throw error;
  },

  async getByTabItemIds(tabItemIds: string[]): Promise<Map<string, TabItemModifier[]>> {
    const byTabItem = new Map<string, TabItemModifier[]>();
    if (tabItemIds.length === 0) return byTabItem;

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('tab_item_modifiers')
      .select('id, tab_item_id, modifier_id, name, price')
      .in('tab_item_id', tabItemIds);
    if (error) throw error;

    for (const row of data ?? []) {
      const tabItemId = row.tab_item_id as string;
      byTabItem.set(tabItemId, [...(byTabItem.get(tabItemId) ?? []), ...(mapTabItemModifierRows([row]) ?? [])]);
    }
    return byTabItem;
  },

  /**
   * Applies `lineDelta` units of the modifiers' current recipes to ingredient
   * stock: negative deducts (items added), positive restores (items removed).
   */
  async applyStockDelta(modifierIds: Array<string | null>, lineDelta: number) {
    if (lineDelta === 0) return;
    const ids = modifierIds.filter((id): id is string => Boolean(id));
    if (ids.length === 0) return;

    const recipes = await productModifiersService.getIngredientsByModifierIds(ids);
    const usage = new Map<string, number>();
    addModifierIngredientUsage(usage, recipes, ids, lineDelta);

    for (const [ingredientId, delta] of usage) {
      await ingredientsService.updateIngredientStockByDelta(ingredientId, delta);
    }
  },
};
